import { FC, memo, useState, useCallback, useRef, useEffect, useMemo } from 'react'
import AvatarEditor from 'react-avatar-editor'
import cn from 'classnames'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { cropperInputSelector } from 'selectors/cropperSelectors'
import Button, { ButtonStyle } from 'components/features/pfm/Button/Button'
import * as styles from 'components/features/pfm/Cropper/Cropper.styl'
import { cropImageResultActionCreator, clearCropperActionCreator } from 'actions/cropperActions'
import { InputRange } from 'components/features/pfm/InputRange/InputRange'
import ButtonRow from 'components/atoms/buttons/ButtonRow'
import SvgIcon from 'components/atoms/SvgIcon'
import RotateSvg from 'icons/rotate.svg'
import { loadBlobImage } from 'helpers/DOMHelpers'

const MIN_SCALE = 1 // we don't allow to scale avatars smaller then crop area, so min scale always 1
const MIN_IMAGE_SIZE = 200

type CropperProps = {
  theme: 'popup' | 'settings'
}

export const Cropper: FC<CropperProps> = memo(({ theme }) => {
  const dispatch = useStoreDispatch()
  const inputImage = useStoreSelector<Blob | null>(cropperInputSelector)
  const [scale, setScale] = useState<number | null>(null)
  const [maxScale, setMaxScale] = useState<number | null>(null)
  const [angle, setAngle] = useState<number>(0)

  const editorRef = useRef<AvatarEditor>(null)

  const handleScaleChange = useCallback<(value: number) => void>((value) => {
    setScale(value)
  }, [])

  const handleCrop = useCallback(async () => {
    if (!editorRef.current) {
      return
    }

    const editor = editorRef.current
    const outputImage = await new Promise<Blob | null>((resolve) => editor.getImage().toBlob(resolve, 'image/jpeg', .9))
    if (!outputImage) {
      return
    }

    dispatch(cropImageResultActionCreator({ image: outputImage }))
  }, [dispatch])

  useEffect(() => {
    return () => {
      dispatch(clearCropperActionCreator())
    }
  }, [dispatch])

  const handleCancel = useCallback(() => dispatch(clearCropperActionCreator()), [dispatch])

  const handleLeft = useCallback(() => {
    setAngle(angle - 90)
  }, [setAngle, angle])

  const handleRight = useCallback(() => {
    setAngle(angle + 90)
  }, [setAngle, angle])

  const actionsChild = useMemo(() => (
    <>
      { theme === 'popup' && (
        <>
          <Button
            fullWidth
            qaMarker='submit-button'
            onClick={ handleCrop }
          >
            Save
          </Button>
          <div className={ styles.cancelLinkWrapper }>
            <a
              data-qa-marker='cancel-button'
              onClick={ handleCancel }
            >
              Cancel
            </a>
          </div>
        </>
      )}
      { theme === 'settings' && (
        <div className={ styles.actions }>
          <ButtonRow
            size='small'
            buttons={ [
              { text: 'Save', size: 'tiny', onClick: handleCrop, qaMarker: 'save-button' },
              { text: 'Cancel', size: 'tiny', style: 'hollow' as ButtonStyle, onClick: handleCancel, qaMarker: 'cancel-button' },
            ] }
          />
        </div>
      )}
    </>
  ), [handleCancel, handleCrop, theme])

  useEffect(() => {(async () => {
    if (!inputImage) {
      return
    }

    const result = await loadBlobImage(inputImage)
    if (!result) {
      return
    }

    const { naturalHeight, naturalWidth } = result
    const size = Math.min(naturalHeight, naturalWidth)
    const nextMaxScale = Math.min(3, size / MIN_IMAGE_SIZE) * 0.99 // multiply by .99 to avoid rounding errors

    const scaleRange = nextMaxScale - MIN_SCALE
    const tenPercentOfRange = scaleRange * .1
    const nextScale = MIN_SCALE + tenPercentOfRange

    setMaxScale(nextMaxScale)
    setScale(nextScale)
  })()}, [inputImage])

  if (!inputImage || maxScale === null || scale === null) {
    return null
  }

  return (
    <div className={ styles.container }>
      <AvatarEditor
        ref={ editorRef }
        image={ inputImage as File }
        width={ 250 }
        height={ 250 }
        border={ 0 }
        color={ [255, 255, 255, 0.6] }
        scale={ scale }
        rotate={ angle }
        borderRadius={ 150 }
      />
      <div className={ styles.rotateButtons }>
        <button type='button' className={ cn(styles.rotateButton, styles.left) } onClick={ handleLeft }>
          <SvgIcon src={ RotateSvg } />
        </button>
        <button type='button' className={ cn(styles.rotateButton, styles.right) } onClick={ handleRight }>
          <SvgIcon src={ RotateSvg } />
        </button>
      </div>
      <div className={ styles.rangeInput }>
        <InputRange
          value={ scale }
          max={ maxScale }
          min={ 1 }
          step={ .01 }
          onChange={ handleScaleChange }
        />
      </div>
      { actionsChild }
    </div>
  )
})

